import { FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import {
  Action,
  Dialog,
  TextField,
} from "@inductiveautomation/ws-components";
import {
  ModalKind,
  ProfileEmail,
  ProfileModalProps,
  ProfileTranslateKey,
} from "./Profile.types";
import { profileEmailSchema } from "./Profile.schemas";
import useLocalizedYupResolver from "../../hooks/localizedYupResolver";

const modalHeading: Record<ModalKind, ProfileTranslateKey | undefined> = {
  none: undefined,
  "add-email": "additional_emails.add_email_action.dialog_headline",
  "change-primary-email": "primary_email.actions.change.heading",
  "change-password": "change_password_action.dialog_headline",
  "delete-account": "delete_account.dialog_headline",
};

const ProfileModal = ({
  currentModal,
  primaryEmail,
  additionalEmails,
  onClose,
  onSubmit,
}: ProfileModalProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "profile" });
  const [fieldValue, setFieldValue] = useState("");

  const resolver = useLocalizedYupResolver(profileEmailSchema);
  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProfileEmail>({
    resolver,
  });

  useEffect(() => {
    setFieldValue("");
    reset();
  }, [currentModal, reset, setFieldValue]);

  if (
    currentModal === "none" ||
    currentModal === "change-primary-email" ||
    currentModal === "change-password"
  )
    return null;

  const headlineKey = modalHeading[currentModal];
  const headline = headlineKey ? t(headlineKey) : "";

  const modalSupportingTextMapping = {
    "add-email": t("additional_emails.add_email_action.dialog_description"),
    "change-password": t("change_password_action.dialog_description", {
      email: primaryEmail,
    }),
    "delete-account": (
      <Trans i18nKey="profile.delete_account.dialog_description">
        {{ email: primaryEmail }}
      </Trans>
    ),
  };

  const hasTextField = ["add-email", "delete-account"].includes(currentModal);

  const emailAlreadyExists =
    currentModal === "add-email" &&
    (fieldValue === primaryEmail ||
      additionalEmails.map((e) => e.email).includes(fieldValue));

  const contentArea = hasTextField ? (
    <TextField
      id={`profile-${currentModal}-modal-email-field`}
      label={
        currentModal === "add-email"
          ? t("additional_emails.add_email_action.dialog_field_label")
          : t("delete_account.field_label")
      }
      labelHidden={currentModal === "delete-account"}
      value={fieldValue}
      handleOnChange={(_id, value) => {
        setValue("email", value);
        setFieldValue(value);
      }}
      hasError={emailAlreadyExists || !!errors.email}
      errorMessage={
        emailAlreadyExists
          ? t("additional_emails.duplicate_error_message")
          : errors.email?.message
      }
      autoFocus
    />
  ) : null;

  const isFieldDisabled = (value: string) => {
    const empty = value.length == 0;
    const deleteEmailDoesNotMatch =
      currentModal === "delete-account" && value !== primaryEmail;

    return empty || deleteEmailDoesNotMatch || emailAlreadyExists;
  };

  const cancelButtonlabel =
    currentModal === "add-email"
      ? t("additional_emails.add_email_action.dialog_cancel_action")
      : t("delete_account.dialog_cancel_action");
  const submitButtonLabel =
    currentModal === "add-email"
      ? t("additional_emails.add_email_action.dialog_submit_action")
      : t("delete_account.dialog_submit_action");

  const submitForm = (e?: FormEvent) =>
    void handleSubmit((data, event) => {
      event?.preventDefault();
      onSubmit(data.email);
    })(e);
  const disabled = isFieldDisabled(fieldValue);

  const modalActions: Action[] = hasTextField
    ? [
        {
          testId: "cancel",
          label: cancelButtonlabel,
          type: "cancel",
        },
        {
          testId: "submit",
          label: submitButtonLabel,
          type: "submit",
          destructive: currentModal === "delete-account",
          disabled,
          onClick: submitForm,
        },
      ]
    : [];

  return (
    <Dialog
      testId={`profile-dialog-${currentModal}`}
      isOpen
      headline={headline}
      supportingText={modalSupportingTextMapping[currentModal]}
      collapseContentScrollGutter
      contentArea={
        <form
          onSubmit={disabled ? (event) => event.preventDefault() : submitForm}
        >
          {contentArea}
        </form>
      }
      actions={modalActions}
      onClose={onClose}
      showCloseButton
      confirmOnClose={!!fieldValue}
    />
  );
};

export default ProfileModal;
